import React, { useState, useEffect, useMemo, useRef } from "react";
import { Card, Button, Offcanvas, Form, Nav, Row, Col } from "react-bootstrap";
import SideNavbar from "./helpers/SideNavbar";
import { useQuery } from "@tanstack/react-query";
import { Link } from 'react-router-dom';
import { useObjectState } from "@uidotdev/usehooks";
import moment from "moment";
import Swal from 'sweetalert2';
import { demo, fetchChallengesByUser, fetchChallengeTypes, useSubmitForm } from "../apis/knackchallenge";
import TimezoneSelect from "react-timezone-select";
import ChallengePostList from "./ChallengePostList";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrashAlt, faEye, faArrowLeft, faArrowRight, faSave } from '@fortawesome/free-solid-svg-icons'; // Import Font Awesome icons
import { FaLinkedin, FaFacebook, FaInstagram } from 'react-icons/fa';
import Spinner from 'react-bootstrap/Spinner';
import axios from 'axios';
import noDataImage from "../assets/nodata.png"
import { useFeatureLimits } from "./FeatureLimits/FeatureLimitsProvider";
import { getAllActivePlatforms } from '../utils/platformUtils';
import { useNavigate } from 'react-router-dom';

const UserID = localStorage.getItem("UserID");
const AccountType = localStorage.getItem("AccountType");

const initialChallenges = {
  UserID,
  ChallengeTypeID: '',
  ChallengeTopic: '',
  StartDate: '',
  EndDate: '',
  Duration_Days: 0,
  ScheduleTime: '',
  TimeZone: '',
};

const Challenges = ({ isLimitReached = false, limitInfo = null }) => {
  const navigate = useNavigate();
  const [newchallengesmode, setNewchallengesmode] = useState(false);
  const [selected, setSelected] = useObjectState(initialChallenges);
  const [viewMode, setViewMode] = useState(false);
  const [showCanvas, setShowCanvas] = useState(false);
  const [step, setStep] = useState(1);
  const [challenges, setChallenges] = useState([]);
  const [showAllChallenges, setShowAllChallenges] = useState(false);
  const viewAllButtonRef = useRef(null);
  const { logFeatureUsage, refreshFeatureLimits } = useFeatureLimits();
  const [isAddMode, setIsAddMode] = useState(false);
  const [shiftForUsagePanel, setShiftForUsagePanel] = useState(() => {
    if (typeof window !== 'undefined') return !!window.featureUsageOpen;
    return false;
  });
  const [enabledPlatforms, setEnabledPlatforms] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]); // Array of platform codes: ['linkedin', 'facebook', 'instagram']
  const [platformsLoading, setPlatformsLoading] = useState(true);
  const roleId = localStorage.getItem('RoleID');
  const backPath = roleId == '4' ? '/Pagehome' : (roleId == '1' ? '/home' : '/adminhome');

  const { data, isLoading } = useQuery(['challengeTypes'], fetchChallengeTypes);
  const { data: challengesData, isLoading: challengesisLoading, refetch } = useQuery(['challenges', UserID], () => fetchChallengesByUser(UserID), { enabled: UserID !== null });
  const { mutate, isLoading: SubmitFormisLoading } = useSubmitForm();

  // ✅ Validate and reset Post Time if StartDate is today and time is in the past
  useEffect(() => {
    if (selected.StartDate && selected.ScheduleTime && selected.TimeZone && !viewMode) {
      // Parse StartDate - handle both UTC and local date formats
      let startDate;
      if (typeof selected.StartDate === 'string') {
        startDate = moment.utc(selected.StartDate).isValid() 
          ? moment.utc(selected.StartDate).startOf('day')
          : moment(selected.StartDate).startOf('day');
      } else {
        startDate = moment.utc(selected.StartDate).startOf('day');
      }
      
      const today = moment.tz(selected.TimeZone).startOf('day');
      
      // If StartDate is today, validate that time is not in the past
      if (startDate.isSame(today, 'day')) {
        const [hours, minutes] = selected.ScheduleTime.split(':');
        if (hours !== undefined && minutes !== undefined && hours !== '' && minutes !== '') {
          const selectedTime = moment.tz(selected.TimeZone)
            .hours(parseInt(hours))
            .minutes(parseInt(minutes))
            .seconds(0)
            .milliseconds(0);
          const currentTime = moment.tz(selected.TimeZone)
            .seconds(0)
            .milliseconds(0);
          
          // Check if selected time is before current time (with 1 minute buffer for safety)
          const currentTimeMinus1 = moment(currentTime).subtract(1, 'minute');
          if (selectedTime.isBefore(currentTimeMinus1)) {
            console.log('⚠️ Past time detected, auto-correcting...', {
              selectedTime: selectedTime.format('HH:mm:ss'),
              currentTime: currentTime.format('HH:mm:ss'),
              startDate: startDate.format('YYYY-MM-DD'),
              today: today.format('YYYY-MM-DD')
            });
            // Auto-correct to next minute
            const nextMinute = moment.tz(selected.TimeZone).add(1, 'minute').startOf('minute');
            setSelected((prev) => ({
              ...prev,
              ScheduleTime: nextMinute.format('HH:mm')
            }));
          }
        }
      }
    }
  }, [selected.StartDate, selected.ScheduleTime, selected.TimeZone, viewMode]); // Re-validate when any of these change


  const handleEdit = (challenge) => {
    const timeOnly = moment(challenge.PostTime, 'YYYY-MM-DD HH:mm:ss').format('HH:mm');
    setSelected({ ...challenge, ScheduleTime: timeOnly, TimeZone: challenge.TimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone, });
    setViewMode(false);
    setStep(1);
    setShowCanvas(true);
    setIsAddMode(false);
    // Don't load platforms for editing - platforms are already set in ChallengePosts
  };

  const handleView = (challenge) => {
    const timeOnly = moment(challenge.PostTime, 'YYYY-MM-DD HH:mm:ss').format('HH:mm');
    setSelected({ ...challenge, ScheduleTime: timeOnly, TimeZone: challenge.TimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone, });
    setViewMode(true);
    setStep(1);
    setShowCanvas(true);
    setIsAddMode(false);
  };




  const handleDelete = async (challengeId) => {
    // Show confirmation dialog first
    const result = await Swal.fire({
      title: 'Delete Challenge',
      text: 'Are you sure you want to delete this challenge? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      console.log('🗑️ Attempting to delete challenge:', challengeId);
      // Send UserID via query to be safe with DELETE semantics
      const response = await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/delete-challenge/${challengeId}?UserID=${encodeURIComponent(UserID)}`);

      console.log('📡 Delete response:', response.data);
      
      if (response.data.success) {
        const remainingCount = response.data.remainingChallenges || 0;
        const usageReduced = response.data.usageReduced || false;
        const diagnostics = response.data.diagnostics;
        
        // Log diagnostics to console for debugging
        console.log('🔍 Challenge deletion diagnostics:', diagnostics);
        
        let messageHtml;
        if (usageReduced) {
          messageHtml = `
            <div>
              <p>Your challenge has been deleted and your usage count has been reduced.</p>
              <p><strong>Remaining challenges:</strong> ${remainingCount}</p>
            </div>
          `;
        } else {
          // Show which condition failed
          let failureReason = '';
          if (diagnostics) {
            if (!diagnostics.isActive) failureReason += 'Challenge is not active. ';
            if (diagnostics.hasPostedPosts) failureReason += 'Challenge has posted content. ';
            if (!diagnostics.isWithinCurrentMonth) failureReason += 'Challenge created outside current month period. ';
          }
          
          messageHtml = `
            <div>
              <p>Your challenge has been deleted successfully.</p>
              <p><strong>Note:</strong> Usage count was not reduced (conditions not met).</p>
              <p><strong>Reason:</strong> ${failureReason || 'Unknown'}</p>
              <p><strong>Remaining challenges:</strong> ${remainingCount}</p>
            </div>
          `;
        }
        
        Swal.fire({
          icon: 'success',
          title: 'Challenge deleted successfully!',
          //html: messageHtml,
          timer: 4000,
          showConfirmButton: false,
        });

        // Refetch challenges instead of reloading the page
        refetch();
        // Refresh feature limits to update usage counts (non-blocking)
        refreshFeatureLimits().catch(err => {
          console.error('❌ Error refreshing feature limits:', err);
          // Don't show error to user, just log it
        });
      }
    } catch (error) {
      console.error('❌ Failed to delete challenge:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      const errorMessage = error.response?.data?.message || 'Server error';
      
      Swal.fire({
        icon: 'error',
        title: 'Cannot delete challenge',
        text: errorMessage,
        confirmButtonText: 'OK'
      });
    }
  };

  const handleTimeConvert = (time, timezone) => time
  // moment
  //   .tz(
  //     `${time}`,     // "YYYY‑MM‑DD HH:mm"
  //     "HH:mm",
  //     timezone
  //   )
  //   .toISOString()// Convert to the specified timezone



  const loadPlatforms = async () => {
    setPlatformsLoading(true);
    const storedAccountType = localStorage.getItem('AccountType');
    
    // For User accounts, automatically use LinkedIn only (no UI needed)
    if (storedAccountType === 'User' || storedAccountType === 'user') {
      setSelectedPlatforms(['linkedin']);
      setEnabledPlatforms([{ name: 'LinkedIn', code: 'linkedin' }]);
      setPlatformsLoading(false);
      return;
    }
    
    // For Page accounts, fetch all active platforms and show selection UI
    try {
      const platforms = await getAllActivePlatforms();
      setEnabledPlatforms(platforms);
      // Default to all platforms selected
      if (platforms.length > 0) {
        setSelectedPlatforms(platforms.map(p => p.code));
      }
      setPlatformsLoading(false);
    } catch (error) {
      console.error('Error fetching active platforms:', error);
      setPlatformsLoading(false);
    }
  };

  const handlePlatformToggle = (platformCode) => {
    if (selectedPlatforms.includes(platformCode)) {
      // Prevent unchecking if it's the last selected platform
      if (selectedPlatforms.length > 1) {
        setSelectedPlatforms(selectedPlatforms.filter(p => p !== platformCode));
      } else {
        Swal.fire({
          icon: 'warning',
          title: 'At Least One Platform Required',
          text: 'Please select at least one platform to post to.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#2e3192',
          timer: 2000
        });
      }
    } else {
      setSelectedPlatforms([...selectedPlatforms, platformCode]);
    }
  };

  const handleUpdateChallenge = async () => {
    // No platform validation needed for edit mode - platforms are already set in ChallengePosts

    // ✅ Validate Post Time - prevent past times when StartDate is today
    if (selected.StartDate && selected.ScheduleTime && selected.TimeZone) {
      const startDate = moment.utc(selected.StartDate).startOf('day');
      const today = moment.tz(selected.TimeZone).startOf('day');
      
      // If StartDate is today, validate that time is not in the past
      if (startDate.isSame(today, 'day')) {
        const [hours, minutes] = selected.ScheduleTime.split(':');
        if (!hours || !minutes || hours === '' || minutes === '') {
          Swal.fire({
            icon: 'error',
            title: 'Invalid Post Time',
            text: 'Please enter a valid post time.',
            showConfirmButton: true,
            confirmButtonColor: '#2e3192'
          });
          return; // Prevent updating
        }
        
        const selectedTime = moment.tz(selected.TimeZone)
          .hours(parseInt(hours))
          .minutes(parseInt(minutes))
          .seconds(0)
          .milliseconds(0);
        const currentTime = moment.tz(selected.TimeZone)
          .seconds(0)
          .milliseconds(0);
        
        // Check if selected time is before current time (with 1 minute buffer)
        const currentTimeMinus1 = moment(currentTime).subtract(1, 'minute');
        if (selectedTime.isBefore(currentTimeMinus1)) {
          Swal.fire({
            icon: 'error',
            title: 'Invalid Post Time',
            html: `You cannot select a past time when the challenge starts today.<br/><br/>Current time: <strong>${currentTime.format('HH:mm')}</strong><br/>Selected time: <strong>${selectedTime.format('HH:mm')}</strong><br/><br/>Please select a future time.`,
            showConfirmButton: true,
            confirmButtonColor: '#2e3192'
          });
          return; // Prevent updating
        }
      }
    }

    try {
      const payload = {
        ChallengeID: selected.ChallengeID,
        UserID: selected.UserID,
        ChallengeTypeID: selected.ChallengeTypeID,
        ChallengeTopic: selected.ChallengeTopic,
        StartDate: selected.StartDate,
        Duration_Days: selected.Duration_Days,
        PostTime: handleTimeConvert(selected.ScheduleTime, selected.TimeZone),
        EndDate: selected.EndDate,
        TimeZone: selected.TimeZone,
        AccountType
        // Don't send platforms in edit mode - platforms are already set in ChallengePosts
      };
      console.log("Payload for update:", payload); // Debugging line

      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/update-challenge`, payload);

      Swal.fire({
        icon: 'success',
        title: response.data.message || 'Challenge updated successfully!',
        showConfirmButton: false,
        timer: 2500,
      });

      refetch();

      setSelected(initialChallenges);
      setShowCanvas(false);
      setIsAddMode(false);
    } catch (error) {
      console.error("Update failed:", error);
      Swal.fire({
        icon: 'error',
        title: 'Update failed!',
        text: error.response?.data?.message || 'Something went wrong.',
      });
    }
  };


  const handleChange = (e) => {
    const { name, value } = e.target;
    const newTime = ''
    if (name === "ScheduleTim") {

      newTime = handleTimeConvert(value, selected.TimeZone);
    }
    
    // ✅ Validate Post Time if StartDate is today - prevent past times
    if (name === "ScheduleTime" && value && selected.StartDate && selected.TimeZone) {
      // Parse StartDate - handle both UTC and local date formats
      let startDate;
      if (typeof selected.StartDate === 'string') {
        startDate = moment.utc(selected.StartDate).isValid() 
          ? moment.utc(selected.StartDate).startOf('day')
          : moment(selected.StartDate).startOf('day');
      } else {
        startDate = moment.utc(selected.StartDate).startOf('day');
      }
      
      const today = moment.tz(selected.TimeZone).startOf('day');
      
      // If StartDate is today, validate that time is not in the past
      if (startDate.isSame(today, 'day')) {
        const [hours, minutes] = value.split(':');
        if (hours && minutes && hours !== '' && minutes !== '') {
          const selectedTime = moment.tz(selected.TimeZone)
            .hours(parseInt(hours))
            .minutes(parseInt(minutes))
            .seconds(0)
            .milliseconds(0);
          const currentTime = moment.tz(selected.TimeZone)
            .seconds(0)
            .milliseconds(0);
          
          console.log('🔍 Validating time on change:', {
            selectedTime: selectedTime.format('HH:mm:ss'),
            currentTime: currentTime.format('HH:mm:ss'),
            startDate: startDate.format('YYYY-MM-DD'),
            today: today.format('YYYY-MM-DD'),
            isBefore: selectedTime.isBefore(currentTime)
          });
          
          // Check if selected time is before current time (with 1 minute buffer)
          const currentTimeMinus1 = moment(currentTime).subtract(1, 'minute');
          if (selectedTime.isBefore(currentTimeMinus1)) {
            Swal.fire({
              icon: 'warning',
              title: 'Invalid Time',
              html: `You cannot select a past time when the challenge starts today.<br/><br/>Current time: <strong>${currentTime.format('HH:mm')}</strong><br/>Selected time: <strong>${selectedTime.format('HH:mm')}</strong><br/><br/>Time will be auto-corrected to the next minute.`,
              showConfirmButton: true,
              confirmButtonColor: '#2e3192'
            });
            // Reset to current time (rounded up to next minute)
            const nextMinute = moment.tz(selected.TimeZone).add(1, 'minute').startOf('minute');
            setSelected((prev) => ({
              ...prev,
              ScheduleTime: nextMinute.format('HH:mm'),
              UserID,
              AccountType,
              PostTime: newTime
            }));
            return; // Don't update with past time
          }
        }
      }
    }
    
    setSelected((prev) => ({ ...prev, [name]: value, UserID, AccountType, PostTime: newTime }));
  };

  const handleSave = async (isUpdate = false) => {
    const requiredFields = [
      "ChallengeTypeID",
      "ChallengeTopic",
      "StartDate",
      "EndDate",
      "Duration_Days",
      "ScheduleTime",
      "TimeZone",
    ];

    for (let field of requiredFields) {
      if (!selected[field]) {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: `Please fill the ${field} field`,
          showConfirmButton: false,
          timer: 3000
        });
        return;
      }
    }

    // ✅ Validate Post Time - prevent past times when StartDate is today
    if (selected.StartDate && selected.ScheduleTime && selected.TimeZone) {
      // Parse StartDate - handle both UTC and local date formats
      let startDate;
      if (typeof selected.StartDate === 'string') {
        // Try parsing as UTC first, then as local date, then try different formats
        if (moment.utc(selected.StartDate).isValid()) {
          startDate = moment.utc(selected.StartDate).startOf('day');
        } else if (moment(selected.StartDate, 'YYYY-MM-DD').isValid()) {
          startDate = moment(selected.StartDate, 'YYYY-MM-DD').startOf('day');
        } else {
          startDate = moment(selected.StartDate).startOf('day');
        }
      } else {
        startDate = moment.utc(selected.StartDate).startOf('day');
      }
      
      // Get today's date in the selected timezone
      const today = moment.tz(selected.TimeZone).startOf('day');
      
      // Also check if startDate (in UTC) matches today (in timezone) by converting both to same timezone
      const startDateInTz = moment.tz(startDate.format('YYYY-MM-DD'), selected.TimeZone).startOf('day');
      
      console.log('🔍 Date validation before save:', {
        startDateUTC: startDate.format('YYYY-MM-DD'),
        startDateInTz: startDateInTz.format('YYYY-MM-DD'),
        today: today.format('YYYY-MM-DD'),
        isSameUTC: startDate.isSame(today, 'day'),
        isSameTz: startDateInTz.isSame(today, 'day'),
        startDateValue: selected.StartDate,
        timezone: selected.TimeZone
      });
      
      // If StartDate is today (check both UTC and timezone comparisons), validate that time is not in the past
      if (startDate.isSame(today, 'day') || startDateInTz.isSame(today, 'day')) {
        const [hours, minutes] = selected.ScheduleTime.split(':');
        if (!hours || !minutes || hours === '' || minutes === '') {
          Swal.fire({
            icon: 'error',
            title: 'Invalid Post Time',
            text: 'Please enter a valid post time.',
            showConfirmButton: true,
            confirmButtonColor: '#2e3192'
          });
          return; // Prevent saving
        }
        
        const selectedTime = moment.tz(selected.TimeZone)
          .hours(parseInt(hours))
          .minutes(parseInt(minutes))
          .seconds(0)
          .milliseconds(0);
        const currentTime = moment.tz(selected.TimeZone)
          .seconds(0)
          .milliseconds(0);
        
        console.log('🔍 Time validation before save:', {
          selectedTime: selectedTime.format('HH:mm:ss'),
          currentTime: currentTime.format('HH:mm:ss'),
          timezone: selected.TimeZone,
          isBefore: selectedTime.isBefore(currentTime)
        });
        
        // Check if selected time is before current time (with 1 minute buffer)
        const currentTimeMinus1 = moment(currentTime).subtract(1, 'minute');
        if (selectedTime.isBefore(currentTimeMinus1)) {
          Swal.fire({
            icon: 'error',
            title: 'Invalid Post Time',
            html: `You cannot select a past time when the challenge starts today.<br/><br/>Current time: <strong>${currentTime.format('HH:mm')}</strong><br/>Selected time: <strong>${selectedTime.format('HH:mm')}</strong><br/><br/>Please select a future time.`,
            showConfirmButton: true,
            confirmButtonColor: '#2e3192'
          });
          return; // Prevent saving
        }
      }
    }

    if (isUpdate) {
      handleUpdateChallenge(); // call custom update logic
    } else {
      // Prepare data for backend - only send fields the backend expects
      const challengeData = {
        UserID: selected.UserID,
        AccountType: selected.AccountType,
        ChallengeTypeID: selected.ChallengeTypeID,
        ChallengeTopic: selected.ChallengeTopic,
        StartDate: selected.StartDate,
        Duration_Days: selected.Duration_Days,
        ScheduleTime: selected.ScheduleTime,
        TimeZone: selected.TimeZone
      };
      
      console.log('📤 Sending challenge data:', challengeData);
      
      // Validate all required fields are present
      const missingFields = [];
      if (!challengeData.UserID) missingFields.push('UserID');
      if (!challengeData.AccountType) missingFields.push('AccountType');
      if (!challengeData.ChallengeTypeID) missingFields.push('ChallengeTypeID');
      if (!challengeData.ChallengeTopic) missingFields.push('ChallengeTopic');
      if (!challengeData.StartDate) missingFields.push('StartDate');
      if (!challengeData.Duration_Days) missingFields.push('Duration_Days');
      if (!challengeData.ScheduleTime) missingFields.push('ScheduleTime');
      if (!challengeData.TimeZone) missingFields.push('TimeZone');
      
      if (missingFields.length > 0) {
        console.error('❌ Missing required fields:', missingFields);
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: `Missing required fields: ${missingFields.join(', ')}`,
          showConfirmButton: false,
          timer: 3000
        });
        return;
      }

      // Validate that at least one platform is selected
      if (!selectedPlatforms || selectedPlatforms.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'No Platform Selected',
          text: 'Please select at least one platform to post to.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#2e3192',
          timer: 2000
        });
        return;
      }
      
      // Add platforms to challengeData
      challengeData.platforms = selectedPlatforms;
      
      mutate(challengeData, {
        onSuccess: (data) => {
          console.log('✅ Challenge creation successful:', data);
          console.log('✅ Response data:', JSON.stringify(data, null, 2));
          Swal.fire({
            position: 'center',
            icon: 'success',
            title: 'Challenge saved successfully!',
            showConfirmButton: false,
            timer: 3000
          });
          // Note: Usage logging is handled by the backend API
          refetch()
          // Refresh feature limits to update usage counts (non-blocking)
          // Add small delay to ensure backend has processed the usage logging
          setTimeout(() => {
            console.log('🔄 Calling refreshFeatureLimits...');
            refreshFeatureLimits().then(() => {
              console.log('✅ Feature limits refreshed successfully');
            }).catch(err => {
              console.error('❌ Error refreshing feature limits:', err);
              // Don't show error to user, just log it
            });
          }, 500); // 500ms delay to ensure backend processing is complete
          setSelected(initialChallenges);
          setStep(1);
          setShowCanvas(false);
          setIsAddMode(false);
        },
        onError: (error) => {
          console.error("Error saving challenge:", error);
          Swal.fire({
            position: 'center',
            icon: 'error',
            title: 'Failed to save challenge. Please try again.',
            showConfirmButton: false,
            timer: 3000
          });
        },
      });
    }
  };


  useEffect(() => {
    if (selected && selected.StartDate && selected.Duration_Days) {
      const durationDays = parseInt(selected.Duration_Days);
      const EndDate = moment.utc(selected.StartDate).add(durationDays, 'days').format('YYYY-MM-DD');
      setSelected(prev => ({ ...prev, EndDate }));
    }
  }, [selected?.StartDate, selected?.Duration_Days]);

  useEffect(() => {
    function onToggle(e){ setShiftForUsagePanel(!!(e && e.detail)); }
    window.addEventListener('featureUsageToggle', onToggle);
    // sync once on mount
    if (typeof window !== 'undefined') setShiftForUsagePanel(!!window.featureUsageOpen);
    return () => window.removeEventListener('featureUsageToggle', onToggle);
  }, []);

  // Reset to show only 3 challenges when data changes (after create/update/delete)
  useEffect(() => {
    if (challengesData && challengesData.length > 0) {
      setShowAllChallenges(false);
    }
  }, [challengesData?.length]);

  // Sort challenges by StartDate descending (most recent first) and limit to 3 initially
  const sortedChallenges = useMemo(() => {
    if (!challengesData || challengesData.length === 0) return [];
    return [...challengesData].sort((a, b) => {
      const dateA = moment(a.StartDate);
      const dateB = moment(b.StartDate);
      return dateB.isBefore(dateA) ? -1 : dateB.isAfter(dateA) ? 1 : 0;
    });
  }, [challengesData]);

  const displayedChallenges = useMemo(() => {
    if (showAllChallenges) {
      return sortedChallenges;
    }
    return sortedChallenges.slice(0, 3);
  }, [sortedChallenges, showAllChallenges]);

  const hasMoreChallenges = sortedChallenges.length > 3;

  const handleViewAll = () => {
    setShowAllChallenges(true);
    // Smooth scroll to top of challenge list after a brief delay
    setTimeout(() => {
      if (viewAllButtonRef.current) {
        viewAllButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  return (
    <SideNavbar>
      <div className="card shadow-sm p-3" style={{ border: 'none', backgroundColor: '#fff' }}>
        <div className="card-header d-flex justify-content-between" style={{ border: 'none', backgroundColor: '#fff' }}>
          <Link to={backPath} className="text-decoration-none">
            <h5 className="welcome-text mt-3" style={{ fontSize: '20px', color: '#2e3192', textAlign: 'left' }}>
              <i className="fa fa-arrow-left" aria-hidden="true" style={{ marginRight: '12px' }}></i>
              Challenge List
            </h5>
          </Link>
          <Button
            id="add-challenge-btn"
            className="custom-btn"
            style={{ 
              transition: 'margin-right 0.2s ease', 
              marginRight: shiftForUsagePanel ? 200 : 0,
              opacity: isLimitReached ? 0.5 : 1,
              cursor: isLimitReached ? 'not-allowed' : 'pointer'
            }}
            disabled={isLimitReached}
            onClick={async () => {
              if (isLimitReached) return;
              setSelected({
                UserID,
                ChallengeTypeID: '',
                ChallengeTopic: '',
                StartDate: moment().add(0, 'days').format('YYYY-MM-DD'),
                EndDate: '',
                Duration_Days: 1,
                ScheduleTime: moment().format('HH:mm'),
                TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              });
              setViewMode(false);
              setIsAddMode(true);
              setStep(1);
              setShowCanvas(true);
              // Load platforms when opening add mode
              await loadPlatforms();
            }}
            title={isLimitReached ? "Challenge limit reached. Upgrade to create more challenges." : "Add New Challenge"}
          >
            <FontAwesomeIcon icon={faPlus} style={{ fontSize: 14 }} /> {/* Add icon */}
          </Button>
        </div>
        <hr />
        
        {/* Subtle limit reached info */}
        {isLimitReached && limitInfo && (
          <div className="alert alert-info mb-3" style={{ 
            backgroundColor: '#e3f2fd', 
            border: '1px solid #bbdefb', 
            borderRadius: '6px',
            padding: '8px 12px',
            margin: '0 0 15px 0'
          }}>
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <i className="fa fa-info-circle me-2" style={{ color: '#1976d2' }}></i>
                <small style={{ color: '#1976d2' }}>
                  <strong>Challenge limit reached:</strong> You've used all {limitInfo.limit?.limit_value || 2} challenges. 
                  You can view and manage existing challenges below, but cannot create new ones.
                </small>
              </div>
            </div>
          </div>
        )}
        <style>
          {`
    .file-input {
      display: none;
    }

    .custom-btn {
      display: inline-block;
      background-color: #2e3192;
      color: white;
      border-radius: 5px;
      cursor: pointer;
      text-align: center;
    }

    .custom-btn:hover {
      background-color: #1c1f6d;
    }
      .custom-tab .nav-link.active {
  background-color: #2e3192 !important;  /* Same as .custom-btn */
  color: white !important;
  border-radius: 5px;
}
  `}
        </style>
        {challengesisLoading && (
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <Spinner animation="border" role="status" style={{ width: '50px', height: '50px' }}>
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p style={{ color: '#2e3192', fontSize: '20px' }}>Loading challenges...</p>
          </div>
        )}
        {!challengesisLoading && sortedChallenges?.length === 0 ? (
          <div style={{ textAlign: 'center' }}>
            <img src={noDataImage} alt="No Data" style={{ width: '50%' }} />
            <p style={{ color: '#2e3192', fontSize: '20px' }}>No posts available</p>
          </div>
        ) : (
          <div>
            <div className="row">
              {!challengesisLoading && displayedChallenges
                ?.map((challenge1) => {
                // Check if challenge is completed (EndDate is before today, not including today)
                const isCompleted = challenge1.EndDate && moment(challenge1.EndDate).startOf('day').isBefore(moment().startOf('day'));
                
                return (
                  <div key={challenge1.ChallengeID} className="col-md-6 col-lg-4 mt-3">
                    <Card className="h-100">
                      <Card.Body style={isCompleted ? { 
                        opacity: 0.5, 
                        backgroundColor: '#f8f9fa',
                        color: '#6c757d'
                      } : {}}>
                        <Card.Title style={isCompleted ? { color: '#6c757d' } : {}}>
                          {challenge1.ChallengeTopic}
                          {isCompleted && <span className="badge bg-secondary ms-2">Completed</span>}
                        </Card.Title>
                        <Card.Text style={isCompleted ? { color: '#6c757d' } : {}}>
                          <b style={{ color: isCompleted ? '#6c757d' : '#2e3192' }}>Type:</b>{" "}
                          {!isLoading &&
                            data?.find((type) => type.ChallengeTypeID === challenge1.ChallengeTypeID)?.ChallengeType
                          } <br />
                          <b style={{ color: isCompleted ? '#6c757d' : '#2e3192' }}>Start Date:</b> {moment.utc(challenge1.StartDate).format('MM-DD-YYYY')} <br />
                          <b style={{ color: isCompleted ? '#6c757d' : '#2e3192' }}>Duration:</b> {challenge1.Duration_Days > 1 ? `${challenge1?.Duration_Days} Days` : `${challenge1.Duration_Days} Day`} <br />
                          <b style={{ color: isCompleted ? '#6c757d' : '#2e3192' }}>Post Time:</b> {moment(challenge1.PostTime, 'YYYY-MM-DD HH:mm:ss').format('HH:mm')} <br />
                        </Card.Text>
                      </Card.Body>
                      <Card.Footer className="d-flex justify-content-between">
                        <Button 
                          className="custom-btn" 
                          size="sm" 
                          onClick={() => handleView(challenge1)}
                          style={{ padding: '8px 16px', fontSize: '12px' }}
                        >
                          <FontAwesomeIcon icon={faEye} style={{ fontSize: '12px' }} />
                        </Button>
                        <Button 
                          className="custom-btn" 
                          size="sm" 
                          onClick={() => handleEdit(challenge1)}
                          disabled={isCompleted}
                          style={isCompleted ? { 
                            opacity: 0.5, 
                            cursor: 'not-allowed',
                            backgroundColor: '#6c757d',
                            borderColor: '#6c757d',
                            padding: '8px 16px',
                            fontSize: '12px'
                          } : { padding: '8px 16px', fontSize: '12px' }}
                        >
                          <FontAwesomeIcon icon={faEdit} style={{ fontSize: '12px' }} />
                        </Button>
                        <Button 
                          className="custom-btn" 
                          size="sm" 
                          onClick={() => handleDelete(challenge1.ChallengeID)}
                          style={{ padding: '8px 16px', fontSize: '12px' }}
                        >
                          <FontAwesomeIcon icon={faTrashAlt} style={{ fontSize: '12px' }} />
                        </Button>
                      </Card.Footer>
                    </Card>
                  </div>
                );
              })}
            </div>
            {!showAllChallenges && hasMoreChallenges && (
              <div className="text-center mt-4" ref={viewAllButtonRef}>
                <Button
                  className="custom-btn"
                  onClick={handleViewAll}
                  style={{ padding: '10px 30px', fontSize: '16px' }}
                >
                  View All Challenges ({sortedChallenges.length})
                </Button>
              </div>
            )}
          </div>
        )}

        <Offcanvas style={{ width: "85%" }} show={showCanvas} onHide={() => setShowCanvas(false)} placement="end" scroll>
          <Offcanvas.Header closeButton >
            <Offcanvas.Title>
              <FontAwesomeIcon onClick={(e) => { e.stopPropagation(); e.preventDefault(); setShowCanvas(false); }} style={{ color: "2e3192", cursor: 'pointer' }} icon={faArrowLeft} /> {/* Left Arrow Icon */}
              {viewMode ? "View Challenge" : `${!isAddMode ? "Edit" : "Add"} Challenge`}</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>


            <Nav variant="tabs" className="mb-3 custom-tab" activeKey={step.toString()} onSelect={(selectedKey) => setStep(Number(selectedKey))}>
              <Nav.Item>
                <Nav.Link eventKey="1">Challenge Setup</Nav.Link>
              </Nav.Item>
              {!isAddMode && (
                <>
                  <Nav.Item>
                    <Nav.Link eventKey="2">Pending Posts</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="3">Approved Posts</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="4">Posted Posts</Nav.Link>
                  </Nav.Item>
                </>
              )}
            </Nav>




            {step === 1 && selected && (

              <Form>
                {SubmitFormisLoading ? <>
                  <div className='spinner'></div>
                </> :
                  <>
                    <div className="row">
                      <div className="col-sm-12 col-lg-6 col-xl-6 col-md-6">
                        <Form.Label>Challenge Type</Form.Label>
                        <Form.Control
                          as="select"
                          name="ChallengeTypeID"
                          value={selected.ChallengeTypeID}
                          onChange={handleChange}
                          disabled={viewMode || !isAddMode}
                        >
                          <option value="">Select Challenge Type</option>
                          {data
                            ?.slice() // create a shallow copy to avoid mutating the original cache
                            .sort((a, b) => a.ChallengeType.localeCompare(b.ChallengeType))
                            .map((type) => (
                              <option key={type.ChallengeTypeID} value={type.ChallengeTypeID}>
                                {type.ChallengeType}
                              </option>
                            ))}

                        </Form.Control>
                        <Form.Label className="mt-3">Timezone</Form.Label>
                        <TimezoneSelect
                          value={selected.TimeZone}
                          name="TimeZone"
                          onChange={(timezone) => setSelected((prev) => ({ ...prev, TimeZone: timezone.value }))}
                          isDisabled={viewMode || !isAddMode} />
                        <Form.Label className="mt-3">Post Time</Form.Label>
                        <Form.Control
                          type="time"
                          name="ScheduleTime"
                          value={selected.ScheduleTime}
                          onChange={handleChange}
                          readOnly={viewMode}
                          min={(() => {
                            // If StartDate is today, prevent selecting past times
                            if (selected.StartDate && selected.TimeZone) {
                              const startDate = moment.utc(selected.StartDate).startOf('day');
                              const today = moment.tz(selected.TimeZone).startOf('day');
                              
                              // If StartDate is today, set min to current time (rounded up to next minute)
                              if (startDate.isSame(today, 'day')) {
                                const nextMinute = moment.tz(selected.TimeZone).add(1, 'minute').startOf('minute');
                                return nextMinute.format('HH:mm');
                              }
                            }
                            return null; // No restriction if StartDate is in the future
                          })()}
                          onBlur={() => {
                            // Validate time when field loses focus
                            if (selected.StartDate && selected.ScheduleTime && selected.TimeZone) {
                              const startDate = moment.utc(selected.StartDate).startOf('day');
                              const today = moment.tz(selected.TimeZone).startOf('day');
                              
                              if (startDate.isSame(today, 'day')) {
                                const [hours, minutes] = selected.ScheduleTime.split(':');
                                const selectedTime = moment.tz(selected.TimeZone).hours(parseInt(hours)).minutes(parseInt(minutes)).seconds(0);
                                const currentTime = moment.tz(selected.TimeZone);
                                
                                if (selectedTime.isBefore(currentTime)) {
                                  Swal.fire({
                                    icon: 'warning',
                                    title: 'Invalid Time',
                                    text: 'You cannot select a past time when the challenge starts today. Please select a future time.',
                                    showConfirmButton: true,
                                    confirmButtonColor: '#2e3192'
                                  });
                                  // Reset to current time (rounded up to next minute)
                                  const nextMinute = moment.tz(selected.TimeZone).add(1, 'minute').startOf('minute');
                                  setSelected((prev) => ({
                                    ...prev,
                                    ScheduleTime: nextMinute.format('HH:mm')
                                  }));
                                }
                              }
                            }
                          }} />
                        <Form.Label className="mt-3" style={{ display: 'none' }}>End Date</Form.Label>
                        <Form.Control
                          type="date"
                          name="EndDate"
                          value={selected.EndDate ? moment(selected.EndDate).format('YYYY-MM-DD') : ''}
                          disabled={true}
                          style={{ display: 'none' }} />
                      </div>
                      <div className="col-sm-12 col-lg-6 col-xl-6 col-md-6">
                        <Form.Label>Challenge Topic</Form.Label>
                        <Form.Control
                          type="text"
                          name="ChallengeTopic"
                          value={selected.ChallengeTopic}
                          onChange={handleChange}
                          readOnly={viewMode || !isAddMode} />
                        <Form.Label className="mt-3">Start Date</Form.Label>
                        <Form.Control
                          type="date"
                          name="StartDate"
                          value={selected.StartDate ? moment.utc(selected.StartDate).format('YYYY-MM-DD') : ''}
                          onChange={handleChange}
                          onFocus={() => {
                            if (!selected.TimeZone) {
                              Swal.fire({
                                icon: 'warning',
                                title: 'Please select a Timezone first',
                                showConfirmButton: true,
                              });
                            }
                          }}
                          readOnly={viewMode || !isAddMode}
                          min={selected.TimeZone
                            ? moment.tz(selected.TimeZone).format('YYYY-MM-DD')
                            : moment().format('YYYY-MM-DD') // fallback
                          } />
                        <Form.Label className="mt-3">Duration</Form.Label>
                        <Form.Control
                          type="number"
                          name="Duration_Days"
                          min={1}
                          max={30}
                          value={selected.Duration_Days}
                          onChange={handleChange}
                          readOnly={viewMode || !isAddMode} />

                      </div>
                    </div>
                    
                    {/* Platform Selection - Only show for Page accounts, when adding new challenge (isAddMode), and when not in view mode */}
                    {isAddMode && !viewMode && !platformsLoading && enabledPlatforms.length > 0 && (localStorage.getItem('AccountType') !== 'User' && localStorage.getItem('AccountType') !== 'user') && (
                      <div style={{
                        marginTop: '20px',
                        padding: '16px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #e0dfdc'
                      }}>
                        <Form.Label style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#2e3192',
                          marginBottom: '12px',
                          display: 'block'
                        }}>
                          Select Platforms to Post To:
                        </Form.Label>
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '16px',
                          alignItems: 'center'
                        }}>
                          {enabledPlatforms.map((platform) => {
                            const platformIcons = {
                              linkedin: <FaLinkedin style={{ color: '#0077b5', fontSize: '24px' }} />,
                              facebook: <FaFacebook style={{ color: '#1877F2', fontSize: '24px' }} />,
                              instagram: <FaInstagram style={{ color: '#E4405F', fontSize: '24px' }} />
                            };
                            const isSelected = selectedPlatforms.includes(platform.code);
                            
                            return (
                              <label
                                key={platform.code}
                                title={platform.name}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  padding: '10px',
                                  borderRadius: '8px',
                                  backgroundColor: isSelected ? '#e8f0fe' : 'white',
                                  border: `2px solid ${isSelected ? '#2e3192' : '#e0dfdc'}`,
                                  transition: 'all 0.2s ease',
                                  userSelect: 'none',
                                  minWidth: '48px',
                                  height: '48px',
                                  position: 'relative'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = isSelected ? '#d0e7ff' : '#f0f0f0';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = isSelected ? '#e8f0fe' : 'white';
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handlePlatformToggle(platform.code)}
                                  style={{
                                    position: 'absolute',
                                    top: '4px',
                                    right: '4px',
                                    width: '16px',
                                    height: '16px',
                                    cursor: 'pointer',
                                    accentColor: '#2e3192',
                                    zIndex: 1
                                  }}
                                />
                                <span style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  {platformIcons[platform.code] || <FaLinkedin style={{ fontSize: '24px' }} />}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                        <small style={{
                          display: 'block',
                          marginTop: '8px',
                          color: '#666',
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          Selected: {selectedPlatforms.length > 0 
                            ? selectedPlatforms.map((code, index) => {
                                const platformIcons = {
                                  linkedin: <FaLinkedin key={index} style={{ color: '#0077b5', fontSize: '16px' }} />,
                                  facebook: <FaFacebook key={index} style={{ color: '#1877F2', fontSize: '16px' }} />,
                                  instagram: <FaInstagram key={index} style={{ color: '#E4405F', fontSize: '16px' }} />
                                };
                                return platformIcons[code] || null;
                              })
                            : 'None'}
                        </small>
                      </div>
                    )}
                  </>
                }

              </Form>
            )}

            {step === 2 && !isAddMode && (
              <ChallengePostList
                challengeId={selected.ChallengeID}
                userId={UserID} status={1} />
            )}
            {step === 3 && !isAddMode && (
              <ChallengePostList
                challengeId={selected.ChallengeID}
                userId={UserID} status={2} />
            )}

            {step === 4 && !isAddMode && (
              <ChallengePostList
                challengeId={selected.ChallengeID}
                userId={UserID} status={3} />
            )}

            <div className={`d-flex ${step === 1 ? "justify-content-end" : "justify-content-between"} mt-4`}>
              {step !== 1 && <Button className="custom-btn" variant="secondary" onClick={() => setStep(step - 1)} disabled={step === 1}>
                <FontAwesomeIcon icon={faArrowLeft} /> {/* Left Arrow Icon */}
              </Button>}
              {step < (isAddMode ? 1 : 4) ? (

                !isAddMode && !viewMode && step === 1 && (
                  <div className="d-flex justify-content-end mb-3">
                    <Button
                      disabled={SubmitFormisLoading}
                      onClick={() => handleSave(true)}
                      style={{
                        backgroundColor: '#2e3192',
                        borderColor: '#007bff',
                        padding: '10px 25px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        borderRadius: '8px',
                        color: 'white'
                      }}
                    >
                      {SubmitFormisLoading ? (
                        <Spinner animation="border" role="status" size="sm">
                          <span className="visually-hidden">Loading...</span>
                        </Spinner>
                      ) : (
                        "Update Challenge"
                      )}
                    </Button>
                  </div>
                ) ||
                <Button className="custom-btn" variant="primary" onClick={() => setStep(step + 1)}>
                  <FontAwesomeIcon icon={faArrowRight} /> {/* Right Arrow Icon */}
                </Button>


              ) : (!viewMode && step === 1) && (
                <div className="d-flex justify-content-end mt-4">
                  {isAddMode ? (
                    <Button
                      disabled={SubmitFormisLoading}
                      onClick={() => handleSave(false)}
                      style={{
                        backgroundColor: '#2e3192',   // Indigo Blue (from your image)
                        borderColor: '#2e3192',
                        padding: '10px 25px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        borderRadius: '8px',
                        color: 'white'
                      }}

                    >
                      Save Challenge
                      {/* {SubmitFormisLoading ? (
                        <Spinner animation="border" role="status" size="sm">
                          <span className="visually-hidden">Loading...</span>
                        </Spinner>
                      ) : (
                      
                      )} */}
                    </Button>
                  ) : (
                    <Button
                      disabled={SubmitFormisLoading}
                      onClick={() => handleSave(true)}
                      style={{
                        backgroundColor: '#007bff',
                        borderColor: '#007bff',
                        padding: '10px 25px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        borderRadius: '8px',
                        color: 'white'
                      }}
                    >
                      Update Challenge
                      {/* {SubmitFormisLoading ? (
                        <Spinner animation="border" role="status" size="sm">
                          <span className="visually-hidden">Loading...</span>
                        </Spinner>
                      ) : (
                        ""
                      )} */}
                    </Button>
                  )}
                </div>
              )}

            </div>
          </Offcanvas.Body>
        </Offcanvas>
      </div>
    </SideNavbar>
  );
};

export default Challenges;
